
import React, { useCallback, useEffect, useState } from "react";
import axios from "../../api/axiosinstance";


const DEFAULT_SETTINGS = {
  isEnabled: false,
  startHour: 21,
  endHour: 23,
  timezone: "Asia/Kolkata",
  leadsPerUserMin: 2,
  leadsPerUserMax: 3,
  batchSize: 2,
  delayMinMinutes: 1,
  delayMaxMinutes: 1,
  protectedDispositions: [],
  excludedUsers: [],
};

function unwrap(response) {
  const value = response?.data ?? response;
  return value?.data ?? value;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
}

export default function LeadShuffle() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [runResult, setRunResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [historyRunId, setHistoryRunId] = useState("");
  const [newDisposition, setNewDisposition] = useState("");

  const request = useCallback(async (method, path, data) => {
    const response = await axios({
      method,
      url: `leadShuffle/${path}`,
      ...(data !== undefined ? { data } : {}),
    });
    return unwrap(response);
  }, []);

  const loadSettings = useCallback(async () => {
    const result = await request("get", "settings");
    if (result?.success === false) {
      throw new Error(result.message || "Settings load failed");
    }
    const data = result?.data ?? result;
    setSettings({ ...DEFAULT_SETTINGS, ...data });
  }, [request]);

  const loadHistory = useCallback(async (runId = "") => {
    const suffix = runId
      ? `?runId=${encodeURIComponent(runId)}`
      : "";
    const result = await request("get", `history${suffix}`);

    if (result?.success === false) {
      throw new Error(result.message || "History load failed");
    }

    const data = result?.data ?? result;
    const items = Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : [];

    setHistory(items);
  }, [request]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      await Promise.all([loadSettings(), loadHistory()]);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err.message ||
        "Unable to load shuffle data."
      );
    } finally {
      setLoading(false);
    }
  }, [loadSettings, loadHistory]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateField = (field, value) => {
    setSettings((current) => ({ ...current, [field]: value }));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");

    if (Number(settings.leadsPerUserMin) < 1 ||
        Number(settings.leadsPerUserMax) < Number(settings.leadsPerUserMin)) {
      setError("Per-user lead limits are invalid.");
      return;
    }

    if (Number(settings.batchSize) < 1 ||
        Number(settings.delayMinMinutes) < 0 ||
        Number(settings.delayMaxMinutes) < Number(settings.delayMinMinutes)) {
      setError("Batch size or delay settings are invalid.");
      return;
    }

    setSaveLoading(true);
    try {
      const payload = {
        ...settings,
        startHour: Number(settings.startHour),
        endHour: Number(settings.endHour),
        leadsPerUserMin: Number(settings.leadsPerUserMin),
        leadsPerUserMax: Number(settings.leadsPerUserMax),
        batchSize: Number(settings.batchSize),
        delayMinMinutes: Number(settings.delayMinMinutes),
        delayMaxMinutes: Number(settings.delayMaxMinutes),
        isEnabled: Boolean(settings.isEnabled),
        protectedDispositions: settings.protectedDispositions,
        excludedUsers: settings.excludedUsers,
      };

      const result = await request("put", "settings", payload);
      if (result?.success === false) {
        throw new Error(result.message || "Settings save failed");
      }

      await loadSettings();
      setNotice("Settings saved successfully.");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err.message ||
        "Could not save settings."
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    setError("");
    setNotice("");
    try {
      const result = await request("get", "preview");
      if (result?.success === false) {
        throw new Error(result.message || "Preview failed");
      }
      setPreview(result?.data ?? result);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err.message ||
        "Could not load preview."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const runManualShuffle = async () => {
    const confirmed = window.confirm(
      "This will change real lead owners. Continue with manual shuffle?"
    );
    if (!confirmed) return;

    setRunLoading(true);
    setError("");
    setNotice("");
    try {
      const result = await request("post", "run", {});
      if (result?.success === false) {
        throw new Error(result.message || "Manual shuffle failed");
      }

      const data = result?.data ?? result;
      setRunResult(data);
      setHistoryRunId(data?.runId || "");
      setNotice("Manual shuffle finished. Refreshing history...");
      await Promise.all([
        loadSettings(),
        loadHistory(data?.runId || ""),
      ]);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err.message ||
        "Manual shuffle failed."
      );
    } finally {
      setRunLoading(false);
    }
  };

  const toggleDisposition = (value) => {
    setSettings((current) => {
      const exists = current.protectedDispositions.some(
        (item) => item.toLowerCase() === value.toLowerCase()
      );
      return {
        ...current,
        protectedDispositions: exists
          ? current.protectedDispositions.filter(
              (item) => item.toLowerCase() !== value.toLowerCase()
            )
          : [...current.protectedDispositions, value],
      };
    });
  };

  const addDisposition = () => {
    const value = newDisposition.trim();
    if (!value) return;

    const exists = settings.protectedDispositions.some(
      (item) => item.toLowerCase() === value.toLowerCase()
    );
    if (!exists) {
      updateField("protectedDispositions", [
        ...settings.protectedDispositions,
        value,
      ]);
    }
    setNewDisposition("");
  };

  if (loading) {
    return <div className="lead-shuffle-page"><p>Loading Lead Shuffle...</p></div>;
  }

  return (
    <main className="lead-shuffle-page">
      <header className="shuffle-header">
        <div>
          <p className="shuffle-eyebrow">SUPER ADMIN / LEAD MANAGEMENT</p>
          <h1>Lead Shuffle Control Panel</h1>
          <p>Preview assignments, manage rules and review shuffle history.</p>
        </div>
        <button className="shuffle-btn secondary" onClick={refresh}>
          Refresh
        </button>
      </header>

      {error && <div className="shuffle-alert error">{error}</div>}
      {notice && <div className="shuffle-alert success">{notice}</div>}

      <section className="shuffle-stats">
        <article className="shuffle-stat">
          <span>Automatic Shuffle</span>
          <strong>{settings.isEnabled ? "Enabled" : "Disabled"}</strong>
          <small>{settings.timezone}</small>
        </article>
        <article className="shuffle-stat">
          <span>Schedule Window</span>
          <strong>
            {String(settings.startHour).padStart(2, "0")}:00–{String(settings.endHour).padStart(2, "0")}:00
          </strong>
          <small>Local schedule time</small>
        </article>
        <article className="shuffle-stat">
          <span>Per User</span>
          <strong>{settings.leadsPerUserMin}–{settings.leadsPerUserMax} leads</strong>
          <small>Requested per run</small>
        </article>
        <article className="shuffle-stat">
          <span>Batch & Delay</span>
          <strong>{settings.batchSize} leads / batch</strong>
          <small>{settings.delayMinMinutes}–{settings.delayMaxMinutes} min delay</small>
        </article>
      </section>

      <section className="shuffle-card">
        <div className="shuffle-card-heading">
          <div>
            <h2>Shuffle Settings</h2>
            <p>Automatic scheduling stays disabled until you enable it here.</p>
          </div>
        </div>

        <form onSubmit={saveSettings}>
          <div className="shuffle-form-grid">
            <label className="shuffle-switch">
              <input
                type="checkbox"
                checked={Boolean(settings.isEnabled)}
                onChange={(event) => updateField("isEnabled", event.target.checked)}
              />
              <span>Enable automatic shuffle</span>
            </label>

            <label>
              Timezone
              <input value={settings.timezone || ""} readOnly />
            </label>

            <label>
              Start hour (0–23)
              <input type="number" min="0" max="23" value={settings.startHour}
                onChange={(e) => updateField("startHour", e.target.value)} required />
            </label>

            <label>
              End hour (0–23)
              <input type="number" min="0" max="23" value={settings.endHour}
                onChange={(e) => updateField("endHour", e.target.value)} required />
            </label>

            <label>
              Minimum leads per user
              <input type="number" min="1" value={settings.leadsPerUserMin}
                onChange={(e) => updateField("leadsPerUserMin", e.target.value)} required />
            </label>

            <label>
              Maximum leads per user
              <input type="number" min="1" value={settings.leadsPerUserMax}
                onChange={(e) => updateField("leadsPerUserMax", e.target.value)} required />
            </label>

            <label>
              Batch size
              <input type="number" min="1" value={settings.batchSize}
                onChange={(e) => updateField("batchSize", e.target.value)} required />
            </label>

            <label>
              Minimum delay (minutes)
              <input type="number" min="0" value={settings.delayMinMinutes}
                onChange={(e) => updateField("delayMinMinutes", e.target.value)} required />
            </label>

            <label>
              Maximum delay (minutes)
              <input type="number" min="0" value={settings.delayMaxMinutes}
                onChange={(e) => updateField("delayMaxMinutes", e.target.value)} required />
            </label>
          </div>

          <div className="shuffle-actions">
            <button className="shuffle-btn primary" type="submit" disabled={saveLoading}>
              {saveLoading ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </form>
      </section>

      <section className="shuffle-card">
        <div className="shuffle-card-heading">
          <div>
            <h2>Protected Dispositions</h2>
            <p>Selected dispositions will be excluded from shuffle.</p>
          </div>
        </div>

        <div className="disposition-list">
          {settings.protectedDispositions.map((item) => (
            <label className="disposition-chip" key={item}>
              <input
                type="checkbox"
                checked
                onChange={() => toggleDisposition(item)}
              />
              <span>{item}</span>
            </label>
          ))}
        </div>

        <div className="add-disposition">
          <input
            value={newDisposition}
            onChange={(e) => setNewDisposition(e.target.value)}
            placeholder="Add a protected disposition"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addDisposition();
              }
            }}
          />
          <button className="shuffle-btn secondary" onClick={addDisposition} type="button">
            Add
          </button>
        </div>

        <p className="shuffle-hint">
          Changes to protected dispositions are saved when you click Save Settings above.
        </p>
      </section>

      <section className="shuffle-card">
        <div className="shuffle-card-heading">
          <div>
            <h2>Preview & Manual Run</h2>
            <p>Preview is read-only. Manual shuffle changes real lead ownership.</p>
          </div>
        </div>

        <div className="shuffle-actions">
          <button className="shuffle-btn secondary" onClick={loadPreview} disabled={previewLoading}>
            {previewLoading ? "Loading Preview..." : "Preview Assignments"}
          </button>
          <button
            className="shuffle-btn danger"
            onClick={runManualShuffle}
            disabled={runLoading || settings.isEnabled}
            title={settings.isEnabled ? "Disable automatic shuffle before manual run" : ""}
          >
            {runLoading ? "Running..." : "Run Manual Shuffle"}
          </button>
        </div>

        {settings.isEnabled && (
          <p className="shuffle-hint">
            Disable and save automatic shuffle before using manual run.
          </p>
        )}

        {preview && (
          <div className="shuffle-result">
            <h3>Preview Result</h3>
            <p>{preview.message || "Preview loaded. No leads were changed."}</p>
            <div className="shuffle-metrics">
              <span>Users: <strong>{preview.totalUsers ?? 0}</strong></span>
              <span>Selected: <strong>{preview.totalSelected ?? 0}</strong></span>
            </div>
            <div className="shuffle-table-wrap">
              <table className="shuffle-table">
                <thead>
                  <tr>
                    <th>Lead ID</th>
                    <th>Previous Owner</th>
                    <th>Planned New Owner</th>
                    <th>Disposition</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.assignments || []).map((item, index) => (
                    <tr key={item.leadMongoId || index}>
                      <td>{item.leadId || "-"}</td>
                      <td>{item.previousOwner?.name || "-"}</td>
                      <td>{item.plannedNewOwner?.name || "-"}</td>
                      <td>{item.dispositionAtPreview || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {runResult && (
          <div className="shuffle-run-summary">
            <h3>Latest Manual Run</h3>
            <p><strong>Run ID:</strong> {runResult.runId || "-"}</p>
            <p><strong>Assigned:</strong> {runResult.totalAssigned ?? 0}</p>
            <p><strong>Failed:</strong> {runResult.totalFailed ?? 0}</p>
            <p><strong>Started:</strong> {formatDate(runResult.startedAt)}</p>
            <p><strong>Finished:</strong> {formatDate(runResult.finishedAt)}</p>
          </div>
        )}
      </section>

      <section className="shuffle-card">
        <div className="shuffle-card-heading">
          <div>
            <h2>Shuffle History</h2>
            <p>Audit history is retained; records are not deleted by this page.</p>
          </div>
          <button className="shuffle-btn secondary" onClick={() => loadHistory()}>
            All Runs
          </button>
        </div>

        <form
          className="history-filter"
          onSubmit={(e) => {
            e.preventDefault();
            loadHistory(historyRunId).catch((err) =>
              setError(err.message || "Unable to load history")
            );
          }}
        >
          <input
            value={historyRunId}
            onChange={(e) => setHistoryRunId(e.target.value)}
            placeholder="Filter by Run ID (optional)"
          />
          <button className="shuffle-btn secondary" type="submit">Filter</button>
        </form>

        <div className="shuffle-table-wrap">
          <table className="shuffle-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Previous Owner</th>
                <th>New Owner</th>
                <th>Type</th>
                <th>Batch</th>
                <th>Status</th>
                <th>Timestamp</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item) => (
                <tr key={item._id}>
                  <td>
                    <strong>{item.leadId?.leadId || item.leadId || "-"}</strong>
                    {item.runId && <small className="history-run-id">{item.runId}</small>}
                  </td>
                  <td>{item.previousAssignedTo?.name || "-"}</td>
                  <td>{item.newAssignedTo?.name || "-"}</td>
                  <td>{item.assignmentType || "-"}</td>
                  <td>{item.batchNumber ?? "-"}</td>
                  <td>
                    <span className={`history-status ${item.status === "success" ? "ok" : "failed"}`}>
                      {item.status || "-"}
                    </span>
                  </td>
                  <td>{formatDate(item.createdAt)}</td>
                  <td>{item.errorMessage || "-"}</td>
                </tr>
              ))}
              {!history.length && (
                <tr><td colSpan="8" className="empty-history">No history records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
